import React, { memo, useMemo, useRef } from 'react';
import useConcurrentEditLock from '@hooks/useConcurrentEditLock';
import './concurentEditButton.scss';
import moment from 'moment';
import { Button } from 'appkit-react';
import { useSelector } from 'react-redux';
import alert from '@components/alert';
import { NETWORK_ERROR } from '@model';

const getCapital = (str) => {
  const arr = str.split(' ');
  if (arr.length === 1) {
    return arr[0].slice(0, 1);
  }
  return `${arr[0].slice(0, 1)}${arr[arr.length - 1].slice(0, 1)}`;
};

const UserEditing = memo(function ({ name }) {
  return (
    <div className="user-editing">
      <div className="user-avatar">{getCapital(name)}</div>
      <div className="user-editing-text">
        <span>{name}</span>
        <span>is editing…</span>
      </div>
    </div>
  );
});

const RefreshData = memo(function ({ handleRefresh = () => {} }) {
  return (
    <div className="refresh-data">
      <div>Data Updated</div>
      <a onClick={handleRefresh}>Refresh</a>
    </div>
  );
});

const getDefaultAlertText = (email) =>
  `Tiles are currently locked for editing by ${email}.`;

function ConcurrentEditButton({
  buttonProps,
  identifier,
  handleRefresh,
  autoRefresh,
  updateTimeFieldName = 'navigatorLastUpdateTime',
  getAlertText = getDefaultAlertText,
}) {
  const { lockData, setLock, loading, handlePolling } = useConcurrentEditLock(
    {},
  );
  const previousLockData = useRef(null);
  const userInfo = useSelector(({ login }) => login.userInfo);

  const refreshData = async () => {
    await handleRefresh();
    await handlePolling(false);
  };

  const getRefreshStatus = (lockData) => {
    // const { navigatorLastUpdateTime } = lockData;
    const shouldRefresh =
      previousLockData.current &&
      moment(previousLockData.current?.[updateTimeFieldName]).valueOf() <
        moment(lockData?.[updateTimeFieldName]).valueOf();
    if (lockData[updateTimeFieldName] === null) {
      lockData[updateTimeFieldName] = new Date().valueOf();
    }
    return shouldRefresh;
  };

  const { buttonDisabled, shouldRefresh, showEditing, lockedUserName } =
    useMemo(() => {
      if (!lockData) {
        return { buttonDisabled: true };
      }
      const { lockInfo /*, navigatorLastUpdateTime*/ } = lockData;
      const lockInfoItem = lockInfo?.find(
        (i) => i.moduleIdentifier === identifier && i.lockStatus,
      );

      const shouldRefresh = getRefreshStatus(lockData);
      if (shouldRefresh && autoRefresh) {
        refreshData();
      }
      previousLockData.current = lockData;

      return {
        buttonDisabled:
          Boolean(lockInfoItem) && lockInfoItem.userEmail !== userInfo.email,
        shouldRefresh,
        showEditing: Boolean(lockInfoItem),
        lockedUserName: lockInfoItem?.userName,
      };
    }, [lockData, identifier, userInfo, handleRefresh]);

  const enterEdit = async () => {
    try {
      const latestLockInfo = await handlePolling(false);
      const shouldRefresh = getRefreshStatus(latestLockInfo);
      if (shouldRefresh && autoRefresh) {
        await refreshData();
      }
      const res = await setLock({
        moduleIdentifier: identifier,
        userEmail: userInfo?.email,
        userName: userInfo?.name,
      });
      const isSameUserEnter =
        res?.userEmail === userInfo?.email &&
        res?.moduleIdentifier === identifier;
      if (res?.lockSuccessful || isSameUserEnter) {
        buttonProps.onClick();
      } else {
        alert.error({
          className: `edit-lock-warning ${
            res?.userEmail ? 'hide-error-badge' : ''
          }`,
          content: res?.userEmail
            ? getAlertText(res?.userEmail)
            : NETWORK_ERROR.INTERNAL_SERVER_ERROR,
        });
        await handlePolling(false);
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className="concurrent-edit-button-wrapper">
      <div className="concurrent-edit">
        {showEditing && !shouldRefresh ? (
          <UserEditing name={lockedUserName || ''} />
        ) : null}
        {shouldRefresh && !autoRefresh ? (
          <RefreshData handleRefresh={refreshData} />
        ) : null}
      </div>
      <Button
        {...buttonProps}
        onClick={enterEdit}
        disabled={buttonDisabled}
        isLoading={loading}
      />
    </div>
  );
}

export default ConcurrentEditButton;
